// «Микрофон занят записью»: общий счётчик записей голоса вне оркестратора.
//
// Голосовое в беседу, диктовка на телефоне, диктовка в заметку, запись в
// Конструкторе — каждая при старте берёт acquireMic(), при остановке, отмене,
// ошибке и размонтировании отпускает. Голосовой оркестратор подписан и на это
// время глушит свой микрофон: иначе он начинает отвечать на то, что диктуют.
//
// Между вкладками одного браузера — BroadcastChannel('agentbox-mic'): вкладка с
// записью раз в HEARTBEAT_MS повторяет «занято», остальные считают её занятой
// не дольше EXPIRE_MS после последнего сигнала. Закрыли вкладку посреди записи
// (или она упала) — «занято» само сходит через ~10 с, вечного занято нет.

export const MIC_CHANNEL = 'agentbox-mic'
const HEARTBEAT_MS = 3_000
const EXPIRE_MS = 10_000

/** Кадр канала: busy — «у меня идёт запись» (повторяется), free — «закончил»,
 *  query — новая вкладка спрашивает, кто сейчас пишет. */
export type MicFrame =
  | { type: 'busy'; tab: string }
  | { type: 'free'; tab: string }
  | { type: 'query'; tab: string }

/** То, что нужно от BroadcastChannel (в тестах — подмена). */
export interface MicChannel {
  postMessage(data: MicFrame): void
  onmessage: ((event: MessageEvent<MicFrame>) => void) | null
  close(): void
}

export interface MicBusyOptions {
  /** null — без связи между вкладками */
  channel?: MicChannel | null
  heartbeatMs?: number
  expireMs?: number
  now?: () => number
}

export interface MicBusy {
  /** Запись началась. Возвращает release — звать сколько угодно раз, считается один. */
  acquire(): () => void
  /** Идёт ли запись здесь или в другой вкладке. */
  isBusy(): boolean
  /** Записей в этой вкладке. */
  localCount(): number
  /** Слушатель зовётся только при смене busy ↔ свободно. */
  subscribe(fn: (busy: boolean) => void): () => void
  /** Отпустить всё и закрыть канал (выгрузка вкладки, тесты). */
  dispose(): void
}

function tabId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  } catch { /* не https — ниже запасной вариант */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

export function createMicBusy(options: MicBusyOptions = {}): MicBusy {
  const channel = options.channel ?? null
  const heartbeatMs = options.heartbeatMs ?? HEARTBEAT_MS
  const expireMs = options.expireMs ?? EXPIRE_MS
  const now = options.now ?? Date.now
  const me = tabId()
  const listeners = new Set<(busy: boolean) => void>()
  /** чужие вкладки с записью → когда последний раз сказали «занято» */
  const remote = new Map<string, number>()
  let count = 0
  let last = false
  let heartbeat: ReturnType<typeof setInterval> | null = null
  let expiry: ReturnType<typeof setTimeout> | null = null
  let disposed = false

  const busy = () => count > 0 || remote.size > 0

  const post = (frame: MicFrame) => {
    if (!channel || disposed) return
    try { channel.postMessage(frame) } catch { /* канал закрыт — не страшно */ }
  }

  const notify = () => {
    const value = busy()
    if (value === last) return
    last = value
    for (const fn of [...listeners]) {
      try { fn(value) } catch (error) { console.error('micBusy: слушатель упал', error) }
    }
  }

  /** Убрать вкладки, которые молчат дольше expireMs, и завести таймер на следующую. */
  const sweep = () => {
    if (expiry) { clearTimeout(expiry); expiry = null }
    const t = now()
    let next = Infinity
    for (const [tab, seen] of remote) {
      const left = seen + expireMs - t
      if (left <= 0) remote.delete(tab)
      else next = Math.min(next, left)
    }
    if (next !== Infinity && !disposed) expiry = setTimeout(sweep, next + 5)
    notify()
  }

  const startHeartbeat = () => {
    post({ type: 'busy', tab: me })
    if (!heartbeat && channel) heartbeat = setInterval(() => post({ type: 'busy', tab: me }), heartbeatMs)
  }

  const stopHeartbeat = () => {
    if (heartbeat) { clearInterval(heartbeat); heartbeat = null }
    post({ type: 'free', tab: me })
  }

  if (channel) {
    channel.onmessage = (event) => {
      const frame = event.data
      if (!frame || typeof frame !== 'object' || typeof frame.tab !== 'string' || frame.tab === me) return
      if (frame.type === 'busy') {
        remote.set(frame.tab, now())
        sweep()
      } else if (frame.type === 'free') {
        remote.delete(frame.tab)
        sweep()
      } else if (frame.type === 'query') {
        if (count > 0) post({ type: 'busy', tab: me })
      }
    }
    post({ type: 'query', tab: me })
  }

  return {
    acquire() {
      if (disposed) return () => undefined
      count += 1
      if (count === 1) startHeartbeat()
      notify()
      let released = false
      return () => {
        if (released || disposed) return
        released = true
        count = Math.max(0, count - 1)
        if (count === 0) stopHeartbeat()
        notify()
      }
    },
    isBusy: busy,
    localCount: () => count,
    subscribe(fn) {
      listeners.add(fn)
      return () => { listeners.delete(fn) }
    },
    dispose() {
      if (disposed) return
      if (count > 0) stopHeartbeat()
      disposed = true
      count = 0
      remote.clear()
      if (expiry) { clearTimeout(expiry); expiry = null }
      if (channel) {
        channel.onmessage = null
        try { channel.close() } catch { /* уже */ }
      }
      notify()
    },
  }
}

function openChannel(): MicChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null
  try {
    return new BroadcastChannel(MIC_CHANNEL) as unknown as MicChannel
  } catch {
    return null
  }
}

/** Один на вкладку. */
const shared = createMicBusy({ channel: openChannel() })

// Вкладку закрывают посреди записи — сразу сказать остальным «свободно»
// (не дожидаясь таймаута). pagehide срабатывает и на iOS, где unload ненадёжен.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', (event) => {
    // persisted — страница ушла в bfcache и может вернуться: канал не закрываем,
    // а замёрзший пульс другие вкладки и так снимут по таймауту
    if (!event.persisted) shared.dispose()
  })
}

export const acquireMic = (): (() => void) => shared.acquire()
export const isMicBusy = (): boolean => shared.isBusy()
export const subscribeMicBusy = (fn: (busy: boolean) => void): (() => void) => shared.subscribe(fn)
